import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Retorna os dados do M & A no CAPS do município
 *
 * @param {int} cod_ibge - IBGE do município
 * @returns {Object|null}
 */
export async function getMaNoCaps(cod_ibge) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/ma-no-caps/${cod_ibge}`
    )
    return data
  } catch (err) {
    console.log(err)
    return null
  }
}

/**
 * Converte data dd/mm/aaaa (ou ddmmyyyy) para yyyy-mm-dd
 * @param {String} dateBr
 * @returns {String|null}
 */
export function formatDateToApi(dateBr) {
  if (dateBr == null || dateBr === '') return null

  const value = String(dateBr).trim()
  if (!value) return null

  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.substring(0, 10)
  }

  const digits = value.replace(/\D/g, '')
  if (digits.length === 8) {
    const day = digits.substring(0, 2)
    const month = digits.substring(2, 4)
    const year = digits.substring(4, 8)
    return `${year}-${month}-${day}`
  }

  const parts = value.split(/[/\-.]/).map((part) => part.trim())
  if (parts.length === 3) {
    const [day, month, year] = parts
    if (day && month && year && String(year).length === 4) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}`
    }
  }

  return null
}

/**
 * Converte Sim/Não ou 1/0 para 1/0
 * @param {*} value
 * @returns {Number}
 */
export function formatSimNaoToApi(value) {
  if (value == null || value === '') return 0
  const normalizado = String(value).trim().toUpperCase()
  if (['1', 'S', 'SIM', 'TRUE'].includes(normalizado)) return 1
  return 0
}

/**
 * Remove o prefixo de código da descrição do select (ex: "01 - Texto" -> "Texto")
 * @param {String|null} text
 * @returns {String|null}
 */
function extractSelectDescription(text) {
  if (!text) return null
  return String(text).replace(/^\d+\s*-\s*/, '').trim() || null
}

/**
 * Monta o payload de Admissão no padrão da API
 * @param {Object} params
 * @param {Object} params.item
 * @param {Object} params.form
 * @param {String|null} params.dtAdmissaoOriginal
 * @param {Array} params.opcoesOrigem
 * @param {Array} params.opcoesDestino
 * @param {String} params.quemAtualizou
 * @returns {Object}
 */
export function buildPayloadAdmissao({
  item,
  form,
  dtAdmissaoOriginal = null,
  opcoesOrigem = [],
  opcoesDestino = [],
  quemAtualizou = '',
}) {
  const origem = opcoesOrigem.find(
    (opcao) => String(opcao.value) === String(form.origem_usuario)
  )
  const destino = opcoesDestino.find(
    (opcao) => String(opcao.value) === String(form.destino_usuario)
  )

  const cidPrincipal = form.cid_principal
    ? String(form.cid_principal).trim()
    : ''
  const cidCausas = form.cid_causas_associadas
    ? String(form.cid_causas_associadas).trim()
    : ''

  return {
    cfcp: item.nu_cpf || item.cpf || item.cfcp || null,
    cnes: item.cnes || item.nu_cnes || item.co_cnes || null,
    dt_admissao_original: formatDateToApi(
      dtAdmissaoOriginal || item.dt_admissao
    ),
    dt_admissao: formatDateToApi(form.dt_admissao),
    prontuario: form.prontuario || null,
    co_origem: form.origem_usuario || null,
    ds_origem:
      extractSelectDescription(origem?.text) || item.ds_origem || null,
    co_cid_principal: cidPrincipal || null,
    cid_causas_associadas: cidCausas || null,
    co_destino: form.destino_usuario || null,
    ds_destino:
      extractSelectDescription(destino?.text) || item.ds_destino || null,
    quem_atualizou: quemAtualizou || null,
  }
}

/**
 * Monta o payload de Conclusão no padrão da API
 * @param {Object} params
 * @param {Object} params.item
 * @param {Object} params.form
 * @param {String|null} params.dtAdmissaoOriginal
 * @param {Array} params.opcoesOrigem
 * @param {Array} params.opcoesDestino
 * @param {String} params.quemAtualizou
 * @returns {Object}
 */
export function buildPayloadConclusao({
  item,
  form,
  dtAdmissaoOriginal = null,
  opcoesOrigem = [],
  opcoesDestino = [],
  quemAtualizou = '',
}) {
  const {
    quem_atualizou,
    ...payloadAdmissao
  } = buildPayloadAdmissao({
    item,
    form,
    dtAdmissaoOriginal,
    opcoesOrigem,
    opcoesDestino,
    quemAtualizou,
  })

  return {
    ...payloadAdmissao,
    dt_conclusao: formatDateToApi(form.dt_conclusao),
    quem_atualizou,
  }
}

/**
 * @deprecated Use buildPayloadAdmissao / buildPayloadConclusao
 */
export function buildPayloadAdmissaoConclusao({
  tipo,
  item,
  form,
  dtAdmissaoOriginal = null,
  opcoesOrigem = [],
  opcoesDestino = [],
  quemAtualizou = '',
}) {
  if (tipo === 'conclusao') {
    return buildPayloadConclusao({
      item,
      form,
      dtAdmissaoOriginal,
      opcoesOrigem,
      opcoesDestino,
      quemAtualizou,
    })
  }

  return buildPayloadAdmissao({
    item,
    form,
    dtAdmissaoOriginal,
    opcoesOrigem,
    opcoesDestino,
    quemAtualizou,
  })
}

/**
 * Salva Admissão/Conclusão do M & A no CAPS
 * Rota de teste — alterar depois para a definitiva
 *
 * @param {Object} payload
 * @returns {Object|null}
 */
export async function salvarAdmissaoConclusao(payload) {
  try {
    const { data } = await Vue.prototype.$api.post(
      '/app/ma-no-caps/enviar-bpa-teste',
      payload
    )
    return data
  } catch (err) {
    console.log(err)
    throw err
  }
}

/**
 * Normaliza os campos do paciente retornado pela API
 *
 * @param {Object} item
 * @returns {Object}
 */
export function normalizePaciente(item) {
  const cnes = item.cnes || item.nu_cnes || item.co_cnes || item.co_ubs
  const ultAtend = item.ult_atend || item.dt_ultimo_acompanhamento
  const emDia =
    item.is_em_dia == 1 ||
    item.is_em_dia === '1' ||
    item.is_faltoso === 0 ||
    item.is_faltoso === '0' ||
    (item.is_em_dia == null &&
      item.is_faltoso == null &&
      ultAtend != null &&
      ultAtend !== '')

  const idade =
    item.idade != null && item.idade !== '' ? Number(item.idade) : null

  return {
    ...item,
    cnes: cnes,
    nu_cnes: cnes,
    idade: idade,
    endereco: item.endereco || item.ds_endereco || '',
    ds_endereco: item.endereco || item.ds_endereco || '',
    ult_atend: ultAtend,
    is_em_dia: emDia ? 1 : 0,
    is_faltoso: emDia ? 0 : 1,
  }
}

/**
 * Normaliza o retorno da API em estabelecimentos e pacientes
 *
 * @param {Object|Array|null} data
 * @returns {{ estabelecimentos: Array, pacientes: Array }}
 */
export function parseMaNoCapsResponse(data) {
  if (!data) {
    return { estabelecimentos: [], pacientes: [] }
  }

  if (data.estabelecimentos && data.pacientes) {
    return {
      estabelecimentos: Array.isArray(data.estabelecimentos)
        ? data.estabelecimentos.map((item) => ({
            ...item,
            nu_cnes: item.cnes || item.nu_cnes || item.co_cnes,
            no_unidade_saude:
              item.no_unidade_saude || item.no_estabelecimento || item.nome || '',
          }))
        : [],
      pacientes: Array.isArray(data.pacientes)
        ? data.pacientes.map(normalizePaciente)
        : [],
    }
  }

  if (
    Array.isArray(data) &&
    data.length > 0 &&
    Array.isArray(data[0]?.pacientes)
  ) {
    const estabelecimentos = []
    const pacientes = []

    data.forEach((item) => {
      const cnes = item.cnes || item.nu_cnes || item.co_cnes
      const nome =
        item.no_unidade_saude || item.no_estabelecimento || item.nome || ''

      estabelecimentos.push({
        ...item,
        nu_cnes: cnes,
        no_unidade_saude: nome,
      })

      ;(item.pacientes || []).forEach((paciente) => {
        pacientes.push(
          normalizePaciente({
            ...paciente,
            cnes: paciente.cnes || paciente.nu_cnes || paciente.co_cnes || cnes,
            no_unidade_saude:
              paciente.no_unidade_saude || nome,
          })
        )
      })
    })

    return { estabelecimentos, pacientes }
  }

  if (Array.isArray(data)) {
    const estabelecimentosMap = new Map()

    data.forEach((item) => {
      const cnes = item.cnes || item.nu_cnes || item.co_cnes || item.co_ubs
      const nome =
        item.no_unidade_saude ||
        item.no_estabelecimento ||
        item.nome_estabelecimento ||
        ''

      if (cnes && !estabelecimentosMap.has(String(cnes))) {
        estabelecimentosMap.set(String(cnes), {
          cnes: cnes,
          nu_cnes: cnes,
          no_unidade_saude: nome,
        })
      }
    })

    return {
      estabelecimentos: Array.from(estabelecimentosMap.values()),
      pacientes: data.map(normalizePaciente),
    }
  }

  return { estabelecimentos: [], pacientes: [] }
}

/**
 * Filtra os pacientes por estabelecimento
 *
 * @param {Array} pacientes
 * @param {string|number} nu_cnes
 * @returns {Array}
 */
export function getPacientesPorEstabelecimento(pacientes, nu_cnes) {
  if (!nu_cnes) {
    return []
  }

  return (pacientes || []).filter((item) => {
    const cnes = item.cnes || item.nu_cnes || item.co_cnes || item.co_ubs
    return String(cnes) === String(nu_cnes)
  })
}

/**
 * Retorna a cor da linha com base no status do paciente
 *
 * @param {Object} item
 * @returns {String}
 */
export function printTableRow(item) {
  if (
    item.is_em_dia == 1 ||
    item.is_em_dia === '1' ||
    item.is_faltoso === 0 ||
    item.is_faltoso === '0' ||
    (item.ult_atend != null && item.ult_atend !== '')
  ) {
    return 'green'
  }
  return 'red'
}

/**
 * Gera o relatório em PDF dos pacientes do CAPS.
 *
 * @param {Array} cadastros
 * @param {Object} selectEstabelecimento
 */
export function generatePdf(cadastros, selectEstabelecimento) {
  let title_pdf = 'Pessoas atendidas'
  let titlePdfSave = utils.methods.titlePdf('ma_no_caps')

  let description_pdf = 'Monitoramento e Acompanhamento no CAPS'
  let headers_pdf = ['NOME', 'NASCIMENTO', 'MÃE', 'ADMISSÃO', 'CONCLUSÃO']
  let data_pdf = []

  for (let i = 0; i < cadastros.length; i++) {
    let obj = {}
    obj.no_cidadao = cadastros[i].no_cidadao || '-'
    obj.dt_nascimento = cadastros[i].dt_nascimento
      ? `${utils.methods.formatDate(cadastros[i].dt_nascimento)}\n(${
          cadastros[i].idade || 0
        } ${cadastros[i].idade > 1 ? 'anos' : 'ano'})`
      : '-'
    obj.no_mae = cadastros[i].no_mae || '-'
    obj.dt_admissao = cadastros[i].dt_admissao
      ? utils.methods.formatDate(cadastros[i].dt_admissao)
      : '-'
    obj.dt_conclusao = cadastros[i].dt_conclusao
      ? utils.methods.formatDate(cadastros[i].dt_conclusao)
      : '-'
    obj.is_em_dia = cadastros[i].is_em_dia
    data_pdf.push(obj)
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })

  let table = utils.methods.createTablePdf(data_pdf)

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Unidade de Saúde',
            value:
              selectEstabelecimento?.no_unidade_saude ||
              selectEstabelecimento?.cnes_nome ||
              '-',
          },
          {
            label: 'CNES',
            value: selectEstabelecimento?.nu_cnes || '-',
          },
        ],
        verticalPosition,
        'l'
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 6,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 6,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          let is_em_dia = data.row.raw[5]
          if (is_em_dia == 1 || is_em_dia == '1') {
            doc.setTextColor(2, 99, 57) // verde
          } else {
            doc.setTextColor(194, 10, 16) // vermelho
          }

          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
          }
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}
